/**
 * Base API utilities for the miniapp.
 * Contains shared functionality used by all API modules.
 */

import { getInitData } from '../lib/telegram';

/**
 * Get API URL - infers from hostname or falls back to env/localhost
 * Priority: hostname inference > VITE_API_URL > localhost
 *
 * Hostname inference: when running on *.chatealopr.com, derives the API URL
 * by replacing 'app' with 'api' in the hostname.
 *   - local-app.chatealopr.com → local-api.chatealopr.com
 *   - dev-app.chatealopr.com   → dev-api.chatealopr.com
 *   - app.chatealopr.com       → api.chatealopr.com
 */
export function getApiUrl(): string {
  if (typeof window !== 'undefined') {
    // Infer API URL from hostname when on a known domain
    const hostname = window.location?.hostname;
    if (hostname?.endsWith('.chatealopr.com')) {
      const apiHostname = hostname.replace('app.', 'api.');
      return `${window.location.protocol}//${apiHostname}`;
    }
  }
  return import.meta.env.VITE_API_URL || 'http://localhost:8787';
}

export const API_URL = getApiUrl();

/**
 * Check if the error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) {
    // TypeError: Failed to fetch - network error
    return true;
  }
  if (error instanceof DOMException && error.name === 'AbortError') {
    // Request was aborted (timeout or manual abort)
    return true;
  }
  return false;
}

/**
 * Get a user-friendly message based on HTTP status code
 */
export function getStatusMessage(status: number): string {
  switch (status) {
    case 400:
      return 'Datos inválidos. Revisa la información e intenta de nuevo.';
    case 401:
      return 'No autorizado. Por favor, reinicia la aplicación.';
    case 403:
      return 'No tienes permiso para realizar esta acción.';
    case 404:
      return 'No se encontró el recurso solicitado.';
    case 429:
      return 'Demasiadas solicitudes. Espera un momento e intenta de nuevo.';
    case 500:
    case 502:
    case 503:
    case 504:
      return 'Error del servidor. Intenta de nuevo más tarde.';
    default:
      return 'Ocurrió un error. Por favor, intenta de nuevo.';
  }
}

/**
 * Base fetch function with init data header
 */
export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const initData = getInitData();

  let response: Response;
  try {
    response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(initData && { 'X-Telegram-Init-Data': initData }),
        ...options.headers,
      },
    });
  } catch (error) {
    // Network error - no connectivity or server unreachable
    if (isNetworkError(error)) {
      throw new Error('Sin conexión. Verifica tu internet e intenta de nuevo.');
    }
    throw new Error('Error de conexión. Intenta de nuevo.');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    // Use the error message from server, or a friendly fallback based on status code
    const errorMessage = error.error || getStatusMessage(response.status);
    const errorWithCode = new Error(errorMessage) as Error & { errorCode?: string };
    if (error.errorCode) {
      errorWithCode.errorCode = error.errorCode;
    }
    throw errorWithCode;
  }

  return response.json();
}

